import React, {useContext, useMemo, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import Button from "../../../../../../components/button/button.jsx";
import {FaPlus} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import {IndividualContext} from "../../individual-context.jsx";
import {v4 as uuidv4} from "uuid";
import GenericEditRemoveCellRenderer
    from "../../../../agents/form/components/tabs/generic-edit-remove-cell-renderer.jsx";
import ConfirmationDeleteList from "../../../../../../components/utils/confirmation-delete-lists.jsx";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import optionsUtils from "../../../../../../utils/options-utils.js";
import {getCities, getNeighborhoods, getStreets} from "../../../../../../utils/location-utils.js";
import InputForm from "../../../../../../components/input/input-form.jsx";
import {MaskDefault} from "../../../../../../utils/mask.js";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import projectUtils from "../../../../../../utils/project-utils.js";
import InputCheckbox from "../../../../../../components/input/input-checkbox.jsx";
import {format} from "date-fns";
import {APIProvider, ControlPosition, Map, Marker} from "@vis.gl/react-google-maps";
import {GeocodeAddress} from "../../../../../../components/utils/map-utils.jsx";
import {useQuery} from "@tanstack/react-query";
import corporationService from "../../../../../../services/web/corporation-service.js";
import {DEFAULT_MAP_ID, normalizeCoordinate} from "../../resources/map-utils.js";
import InputSwitch from "../../../../../../components/input/input-switch.jsx";

const initialData = () => ({
    itemId: uuidv4(),
    city: "",
    date: "",
    type: "",
    number: "",
    address: "",
    zipCode: "",
    complement: "",
    neighborhood: "",
    lat: "",
    lng: "",
});

const getLocationPosition = (location) => {
    if (!location) return null;

    const lat = normalizeCoordinate(typeof location.lat === "function" ? location.lat() : location.lat);
    const lng = normalizeCoordinate(typeof location.lng === "function" ? location.lng() : location.lng);

    if (lat === null || lng === null) return null;

    return {lat, lng};
}

const formatCoordinate = (value) => {
    const parsedValue = normalizeCoordinate(value);
    return parsedValue === null ? "" : parsedValue.toFixed(6);
}

const getInitialMapPosition = (address, corporation) => {
    const addressPosition = getLocationPosition({lat: address?.lat, lng: address?.lng});

    if (addressPosition) return addressPosition;

    return {
        lat: normalizeCoordinate(corporation?.latitude) ?? 0,
        lng: normalizeCoordinate(corporation?.longitude) ?? 0,
    }
}

export const AddressMapModal = ({open, setOpen, address, mapPosition, setMapPosition, onConfirm}) => {
    const {data: corporation, isLoading} = useQuery({
        queryFn: () => corporationService.entity(),
        queryKey: ["corporation"]
    });

    const addressLat = normalizeCoordinate(address?.lat);
    const addressLng = normalizeCoordinate(address?.lng);

    const initialPosition = useMemo(
        () => getInitialMapPosition(address, corporation),
        [address, corporation]
    );

    const selectedPosition = mapPosition ?? initialPosition;
    const hasAddressPosition = addressLat !== null && addressLng !== null;
    const geocodeLatitude = hasAddressPosition ? addressLat : null;
    const geocodeLongitude = hasAddressPosition ? addressLng : null;

    const changePosition = (location) => {
        const position = getLocationPosition(location);
        if (position) setMapPosition(position);
    }

    const handleMapClick = (event) => {
        changePosition(event.detail.latLng);
    }

    const handleConfirm = () => {
        onConfirm(selectedPosition);
        setOpen(false);
    }

    return (
        <Modal open={open} setOpen={setOpen} title={"Mapa"} className={"w-7/12"}>
            <Modal.Body className="!p-0 !m-0 align-self-center justify-items-center">
                {
                    isLoading &&
                    <div
                        className="flex min-h-[420px] items-center justify-center text-sm text-gray-600"
                        style={{height: "calc(-250px + 100vh)"}}
                    >
                        Carregando mapa...
                    </div>
                }

                {
                    !isLoading &&
                    <APIProvider apiKey={corporation?.maps_api_key ?? ""} language={"pt-BR"}>
                        <Map
                            mapId={DEFAULT_MAP_ID}
                            reuseMaps={true}
                            defaultZoom={hasAddressPosition ? 16 : 13}
                            streetViewControl={false}
                            defaultCenter={selectedPosition}
                            gestureHandling={'greedy'}
                            style={{height: "calc(-250px + 100vh)", width: "100%"}}
                            mapTypeControlOptions={{position: ControlPosition.TOP_RIGHT}}
                            fullscreenControlOptions={{position: ControlPosition.BOTTOM_RIGHT}}
                            onClick={handleMapClick}
                        >
                            <GeocodeAddress
                                address={address}
                                changeLocation={changePosition}
                                parsedLatitude={geocodeLatitude}
                                parsedLongitude={geocodeLongitude}
                            />

                            <Marker
                                position={selectedPosition}
                                draggable={true}
                                onDragEnd={(event) => changePosition(event.latLng)}
                            />
                        </Map>
                    </APIProvider>
                }
            </Modal.Body>

            <Modal.Footer>
                <Button target="primary" disabled={isLoading} onClick={handleConfirm}>
                    Confirmar localização
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const Addresses = () => {
    const {individual, setIndividual, setChanges} = useContext(IndividualContext)
    const [openModal, setOpenModal] = useState(false)
    const [openMapModal, setOpenMapModal] = useState(false)
    const [mapPosition, setMapPosition] = useState(null)
    const [data, setData] = useState(initialData)

    const onChangeCustom = (value, id) => setData(prev => ({...prev, [id]: value}))
    const onChange = ({target: {id, value}}) => setData(prev => ({...prev, [id]: value}));
    const onChangeCheckbox = (value) => {
        setIndividual(prev => ({...prev, homeless: value}))
        setChanges(prev => (prev.includes("addresses") ? prev : [...prev, "addresses"]))
    }

    const remove = (data) => {
        ConfirmationDeleteList(() => {
            setIndividual(prev => ({
                ...prev,
                addresses: prev.addresses.filter(dep => dep.itemId !== data.itemId)
            }));

            setChanges(prev => (prev.includes("addresses") ? prev : [...prev, "addresses"]))
        })
    }

    const [columnDefs] = useState([
        { headerName: "Tipo", field: "type", width: 110, comparator: aggridUtils.nullLastComparator },
        { headerName: "Logradouro", field: "address", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "Número", field: "number", width: 80, comparator: aggridUtils.nullLastComparator },
        { headerName: "Bairro", field: "neighborhood", comparator: aggridUtils.nullLastComparator },
        { headerName: "Município", field: "city", flex: 1, comparator: aggridUtils.nullLastComparator },
        { headerName: "CEP", field: "zipCode", width: 90, comparator: aggridUtils.nullLastComparator },
        { headerName: "Data", field: "date", comparator: aggridUtils.nullLastComparator, width: 90, valueFormatter: ({value}) => projectUtils.formatDate(value, "DD/MM/YYYY") },
        {
            headerName: "", field: "", width: 90,
            cellRenderer: GenericEditRemoveCellRenderer,
            cellRendererParams: {setData, setOpen: setOpenModal, remove}
        },
    ]);

    const save = () => {
        if (!data.date) {
            projectUtils.swalErrorMessage("", "O campo \"Data\" é obrigatório.", "Ok");
            return
        }

        const preparedData = {...initialData(), ...data, lat: data.lat ?? "", lng: data.lng ?? ""};

        setIndividual(prevItems => {
            const exists = prevItems.addresses.some(t => t.itemId === preparedData.itemId);

            const updated = exists
                ? prevItems.addresses.map(t => (t.itemId === preparedData.itemId ? preparedData : t))
                : [...prevItems.addresses, preparedData];

            return {
                ...prevItems,
                addresses: updated
            }
        });

        setChanges(prev => (prev.includes("addresses") ? prev : [...prev, "addresses"]))
        onHide()
    }

    const onHide = () => {
        setData(initialData);
        setMapPosition(null);
        setOpenMapModal(false);
        setOpenModal(false)
    }

    const openMap = () => {
        setMapPosition(null);
        setOpenMapModal(true);
    }

    const confirmMapLocation = (position) => {
        setData(prev => ({
            ...prev,
            lat: formatCoordinate(position.lat),
            lng: formatCoordinate(position.lng)
        }));
    }

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>Endereço</Card.Title>
                    <Button className="h-7.5! w-7.5 p-0!" onClick={() => setOpenModal(true)}>
                        <FaPlus/>
                    </Button>
                </Card.Header>
                <Card.Body>
                    <SentryRow className={"mb-2"}>
                        <div className="col-span-12">
                            <InputSwitch
                                id={"homeless"}
                                label={"Situação de rua"}
                                checked={individual.homeless}
                                onChange={(checked) => onChangeCheckbox(checked)}
                            />
                        </div>
                    </SentryRow>
                    <div className={"h-[calc(100vh-222px)]"}>
                        <AgGridReact
                            rowData={individual.addresses}
                            columnDefs={columnDefs}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal overflow={true} isCloseClick={false} open={openModal} setOpen={onHide}  title={"Endereço"} className={"w-5/12"}>
                <Modal.Body>
                    <SentryRow>
                        <div className="col-span-3">
                            <SelectComponent
                                id="type"
                                value={optionsUtils.addressType.find(a => a.value === data.type)}
                                options={optionsUtils.addressType}
                                label={"Tipo"}
                                onChange={(selected) => onChangeCustom(selected?.value, "type")}
                            />
                        </div>
                        <div className="col-span-7">
                            <SelectComponent
                                loadOptions={getStreets}
                                value={optionsUtils.getValue(data.address)}
                                isAsync={true}
                                label={"Logradouro"}
                                onChange={(selected) => onChangeCustom(selected?.value, "address")}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>
                        <div className="col-span-2">
                            <InputForm
                                id="number"
                                value={data.number}
                                label="Número"
                                onChange={onChange}/>
                        </div>

                        <div className="col-span-6">
                            <SelectComponent
                                loadOptions={getNeighborhoods}
                                value={optionsUtils.getValue(data.neighborhood)}
                                isAsync={true}
                                label={"Bairro"}
                                onChange={(selected) => onChangeCustom(selected?.value, "neighborhood")}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>

                        <div className="col-span-6">
                            <SelectComponent
                                loadOptions={getCities}
                                value={optionsUtils.getValue(data.city)}
                                isAsync={true}
                                label={"Município"}
                                onChange={(selected) => onChangeCustom(selected?.value, "city")}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>

                        <div className="col-span-6">
                            <InputForm
                                id="complement"
                                value={data.complement}
                                label="Complemento"
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-span-3">
                            <InputForm
                                id="zipCode"
                                value={data.zipCode}
                                onChange={onChange}
                                label="CEP"
                                mask={MaskDefault.cep}
                            />
                        </div>
                        <div className="col-span-3">
                            <InputDatetime
                                id="date"
                                label="Data *"
                                selected={data.date}
                                onChange={(date) => {
                                    const datePrepared = date ? format(date, "yyyy-MM-dd") : ""
                                    onChangeCustom(datePrepared, "date")
                                }}
                                classNameWrapper={"w-full"}
                                dateFormat="L"
                            />
                        </div>
                        <div className="col-span-5">
                            <InputForm
                                id="lat"
                                value={data.lat}
                                label="Latitude"
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-span-5">
                            <InputForm
                                id="lng"
                                value={data.lng}
                                label="Longitude"
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-span-2 flex">
                            <Button
                                className={"w-full px-0!"}
                                onClick={openMap}
                                title={"Selecionar no mapa"}
                            >
                                Mapa
                            </Button>
                        </div>
                    </SentryRow>
                </Modal.Body>

                <Modal.Footer className={"p-1"}>
                    <div className={"w-full flex justify-end"}>
                        <Button onClick={save}>
                            Confirmar
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <AddressMapModal
                open={openMapModal}
                setOpen={setOpenMapModal}
                address={data}
                mapPosition={mapPosition}
                setMapPosition={setMapPosition}
                onConfirm={confirmMapLocation}
            />
        </>
    );
};

export default Addresses;
